import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { NgxSpinnerModule } from 'ngx-spinner';
import { environment } from '../../../environments/environment';
import type { MileEnvironmentBanner } from '../../../environments/mile-environment-banner.model';
import { NavbarComponent } from '../../shared/components/navbar/navbar.component';
import { SidebarComponent } from '../../shared/components/sidebar/sidebar.component';

@Component({
  selector: 'app-layout-base',
  imports: [
    RouterOutlet,
    CommonModule,
    NavbarComponent,
    SidebarComponent,
    NgxSpinnerModule,
  ],
  templateUrl: './layout-base.component.html',
  styleUrl: './layout-base.component.scss',
})
export class LayoutBaseComponent implements OnDestroy {
  /** Config de ambiente (incl. faixa não-produção); substituído por fileReplacements no build. */
  protected readonly mileEnvironment = environment;

  /**
   * Faixa de ambiente com tipo explícito para o template.
   * O narrowing de `@if (mileEnvironment.environmentBanner; as banner)` no compilador Angular
   * pode inferir só `{ text, cssClass }` e acusar NG9 em `emphasizedPart`.
   */
  protected get activeEnvironmentBanner(): MileEnvironmentBanner | null {
    return this.mileEnvironment.environmentBanner as MileEnvironmentBanner | null;
  }

  isSidebarCollapsed = false;
  /** When true, mouseleave does not schedule collapsing the rail. */
  isSidebarPinned = false;
  title = 'mile-express-front';

  router = inject(Router);

  isSidebarOpen = false;

  /** Delay before collapsing after pointer leaves the sidebar (unpinned only). */
  readonly sidebarCollapseDelayMs = 320;

  private collapseLeaveTimer: ReturnType<typeof setTimeout> | null = null;

  toggleSidebar() {
    this.isSidebarOpen = !this.isSidebarOpen;
  }

  /** Desktop: explicit expand/collapse (md+). Collapsing clears pin. */
  toggleDesktopSidebar() {
    this.clearCollapseTimer();
    this.isSidebarCollapsed = !this.isSidebarCollapsed;
    if (this.isSidebarCollapsed) {
      this.isSidebarPinned = false;
    }
  }

  togglePin() {
    this.isSidebarPinned = !this.isSidebarPinned;
    if (this.isSidebarPinned) {
      this.clearCollapseTimer();
      this.isSidebarCollapsed = false;
    }
  }

  expandSidebar() {
    this.clearCollapseTimer();
    this.isSidebarCollapsed = false;
  }

  collapseSidebar() {
    if (this.isSidebarPinned) {
      return;
    }
    this.clearCollapseTimer();
    this.collapseLeaveTimer = setTimeout(() => {
      this.isSidebarCollapsed = true;
      this.collapseLeaveTimer = null;
    }, this.sidebarCollapseDelayMs);
  }

  private clearCollapseTimer() {
    if (this.collapseLeaveTimer != null) {
      clearTimeout(this.collapseLeaveTimer);
      this.collapseLeaveTimer = null;
    }
  }

  ngOnDestroy(): void {
    this.clearCollapseTimer();
  }

  get currentRoute(): string {
    return this.router.url;
  }
}
